import type { FieldWhere, WhereLeaf } from '../../../query-dsl';
import type { CustomFieldValueType } from '../types';

/**
 * The synthetic column id prefix `mergeCustomFieldColumns` gives every
 * custom-field column it produces (`` `customField.${definitionId}` ``).
 * Shared by the fusion builder (to construct the id) and by
 * `resolveCustomFieldFilter` (to recognize it on the way back from the
 * generic filter UI) so the string literal lives in exactly one place.
 */
export const CUSTOM_FIELD_COLUMN_PREFIX = 'customField.';

/**
 * The typed value column a custom-field `where` fragment targets, one per
 * `custom_field_definition.type` (design.md D2's value-table shape).
 *
 * The Aurora Query DSL filters by camelCase ENTITY PROPERTY names, not
 * database column names — these MUST match `CustomFieldValueRow`'s (and the
 * backend entity's) camelCase properties, the same way `definitionId` does in
 * the same `where` clause. Using the snake_case DB column name here would
 * silently produce a `where` fragment the backend validator/Drizzle
 * translation never matches against the real property.
 */
export type CustomFieldValueColumn =
  'valueText' | 'valueNumber' | 'valueDate' | 'valueBoolean' | 'valueOptionId' | 'valueOptionIds';

/**
 * One row of a consumer's `<entity>CustomFieldValues` relation (e.g.
 * `IamTagCustomFieldValue`), shaped generically so `mergeCustomFieldColumns`
 * can read it for ANY consumer without importing a consumer-specific
 * GraphQL type (HR-PORTS-001 — this module stays `@app`-free). Every field
 * beyond `definitionId` is optional because only ONE is ever populated per
 * row (design.md D2's typed-column routing) and older browsers rendering a
 * newly-added definition may match no row at all.
 */
export interface CustomFieldValueRow {
  readonly definitionId: string;
  readonly valueText?: string | null;
  /**
   * `GraphQLFloat` is a passthrough scalar over the Postgres `numeric`
   * column (`valueNumber`) — the backend's `decimal` type-handler formats it
   * to a JS number on read, so the wire value is already numeric.
   */
  readonly valueNumber?: number | null;
  readonly valueDate?: string | null;
  readonly valueBoolean?: boolean | null;
  readonly valueOptionId?: string | null;
  readonly valueOptionIds?: ReadonlyArray<string> | null;
}

/**
 * Reads the ONE typed field a `CustomFieldValueRow` carries for the given
 * `type` — the accessor half of cell rendering. Returns `undefined` when no
 * value row matched (nothing persisted yet for this definition/parent pair).
 */
export function pickCustomFieldValue(
  row: CustomFieldValueRow | undefined,
  type: CustomFieldValueType,
): unknown {
  if (!row) return undefined;
  switch (type) {
    case 'text':
      return row.valueText ?? undefined;
    case 'number':
      return row.valueNumber ?? undefined;
    case 'date':
      return row.valueDate ?? undefined;
    case 'boolean':
      return row.valueBoolean ?? undefined;
    case 'select':
      return row.valueOptionId ?? undefined;
    case 'multiselect':
      return row.valueOptionIds ?? undefined;
    case 'encrypted':
      // No typed column to read: the API never returns the stored value (masked on every
      // format pass), so the row never carries one (design.md D4).
      return undefined;
  }
}

/**
 * Nests a custom-field value condition under the `customFieldValues`
 * relation, pairing the `definitionId` EXISTS filter with the typed
 * value-column operator — the DSL fragment every synthetic custom-field
 * column compiles to (design.md D4):
 *
 *   nestCustomFieldWhere('def-1', 'valueText', { iLike: '%tag%' })
 *   // → { customFieldValues: { and: [{ definitionId: { eq: 'def-1' } }, { valueText: { iLike: '%tag%' } }] } }
 *
 * This preserves the EXISTS-over-relation semantics of the DSL: the parent
 * rows are narrowed to those with a matching `customFieldValues` child; the
 * loaded children themselves are shaped by `relations`, not by this `where`
 * fragment (see `catalyst-query-dsl`'s "filter parents vs shape children").
 */
export function nestCustomFieldWhere(
  definitionId: string,
  valueColumn: CustomFieldValueColumn,
  condition: WhereLeaf,
): FieldWhere {
  return {
    customFieldValues: {
      and: [{ definitionId: { eq: definitionId } }, { [valueColumn]: condition }],
    },
  };
}

/**
 * Declarative descriptor for a synthetic custom-field column consumed by
 * `buildSearchWhere`'s general (free-text) search. `text` resolves directly
 * to `valueText iLike`. `select`/`multiselect` resolve the search term to
 * matching `optionId`s CLIENT-SIDE — via `resolveOptionIds`, matching
 * against the already-loaded option catalog in the active locale — and
 * NEVER through a server-side join to `option.search_text` (design.md D4):
 * the option catalog lives in the `tools` BC while the value lives in the
 * consumer BC, so a `value → option` join would read another BC's data
 * outside the two named exceptions of HR-BRIDGES-002
 * (the same reason `definitionId` is a soft reference, not a hard FK).
 */
export type CustomFieldSearchColumn =
  | { readonly kind: 'customField'; readonly definitionId: string; readonly type: 'text' }
  | {
      readonly kind: 'customField';
      readonly definitionId: string;
      readonly type: 'select' | 'multiselect';
      readonly resolveOptionIds: (term: string) => string[];
    };

/**
 * Declarative descriptor for a synthetic custom-field column filter
 * consumed by `buildFilterWhere`. Unlike plain columns — whose filter
 * operator comes from the generic UI vocabulary (`contains`/`equals`/...,
 * see `toFilterLeaf`) — a custom-field filter carries the exact DSL leaf
 * condition directly: `select` filters use `eq`/`in` on `valueOptionId`,
 * `multiselect` filters use `contains`/`overlap` on `valueOptionIds`
 * (native DSL array semantics, spec `custom-field-search`).
 */
export interface CustomFieldFilter {
  readonly kind: 'customField';
  readonly definitionId: string;
  readonly valueColumn: CustomFieldValueColumn;
  readonly condition: WhereLeaf;
}

/** Re-exported for consumers that only need the value-type vocabulary. */
export type { CustomFieldValueType };
